import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLiveTime, formatLiveElapsed } from '../hooks/useLiveTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 3000;

const CALL_TYPE_LABEL = {
  call_waiter: { label: 'Ofisiantı çağırır', icon: '🛎️', color: 'bg-amber-500' },
  request_bill: { label: 'Hesab istəyir', icon: '🧾', color: 'bg-indigo-500' },
};

const LiveTimestamp = ({ iso, now, tone = 'light' }) => {
  const { exact, elapsed } = formatLiveElapsed(iso, now);
  const exactCls = tone === 'dark' ? 'text-stone-700' : 'text-white';
  const elapsedCls = tone === 'dark' ? 'text-emerald-700 font-semibold' : 'text-amber-200 font-semibold';
  return (
    <span className="font-mono text-xs" data-testid="live-timestamp">
      <span className={exactCls}>{exact}</span>
      <span className="mx-1 opacity-50">•</span>
      <span className={elapsedCls}>{elapsed}</span>
    </span>
  );
};

export const WaiterPanel = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [readyOrders, setReadyOrders] = useState([]);
  const [pendingConfirm, setPendingConfirm] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resolvingId, setResolvingId] = useState(null);
  const lastCallCount = useRef(0);
  const audioRef = useRef(null);
  const now = useLiveTime(1000);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [restaurantId]);

  const fetchAll = async () => {
    try {
      const [callRes, readyRes, awaitRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/waiter-calls/restaurant/${restaurantId}?status=pending`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/orders/restaurant/${restaurantId}?status=ready`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/orders/restaurant/${restaurantId}?status=awaiting_confirmation`, { withCredentials: true }),
      ]);
      const totalAlerts = callRes.data.length + awaitRes.data.length;
      if (lastCallCount.current > 0 && totalAlerts > lastCallCount.current) {
        try { audioRef.current?.play().catch(() => {}); } catch { /* ignore */ }
      }
      lastCallCount.current = totalAlerts;
      setCalls(callRes.data);
      setReadyOrders(readyRes.data);
      setPendingConfirm(awaitRes.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Yüklənmə xətası');
    } finally {
      setLoading(false);
    }
  };

  const resolveCall = async (callId) => {
    setResolvingId(callId);
    try {
      await axios.patch(`${BACKEND_URL}/api/waiter-calls/${callId}/resolve`, {}, { withCredentials: true });
      await fetchAll();
    } catch (err) {
      alert('Xəta: ' + (err.response?.data?.detail || err.message));
    } finally {
      setResolvingId(null);
    }
  };

  const markServed = async (orderId) => {
    setResolvingId(orderId);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/orders/${orderId}/status`,
        { status: 'served' },
        { withCredentials: true }
      );
      await fetchAll();
    } catch (err) {
      alert('Xəta: ' + (err.response?.data?.detail || err.message));
    } finally {
      setResolvingId(null);
    }
  };

  const confirmAction = async (orderId, action) => {
    setResolvingId(orderId);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/orders/${orderId}/confirm`,
        { action, reason: action === 'reject' ? 'Ofisiant tərəfindən rədd' : null },
        { withCredentials: true }
      );
      await fetchAll();
    } catch (err) {
      alert('Xəta: ' + (err.response?.data?.detail || err.message));
    } finally {
      setResolvingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-indigo-950">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-stone-900 to-indigo-900 text-white">
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=" preload="auto" />

      <header className="sticky top-0 z-10 bg-stone-900/80 backdrop-blur-sm border-b border-stone-700 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-stone-400 hover:text-white"
            data-testid="waiter-back-btn"
          >← Dashboard</button>
          <h1 className="text-xl font-bold" data-testid="waiter-title">🛎️ Ofisiant Paneli</h1>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <Badge label="Təsdiq" value={pendingConfirm.length} accent="bg-rose-600" />
          <Badge label="Çağırış" value={calls.length} accent="bg-amber-500" />
          <Badge label="Hazır sifariş" value={readyOrders.length} accent="bg-emerald-500" />
          <div className="text-xs text-stone-400">Real-time • {POLL_INTERVAL_MS / 1000}s</div>
        </div>
      </header>

      {error && (
        <div className="px-5 py-3 bg-red-900/40 text-red-200 text-sm" data-testid="waiter-error">⚠️ {error}</div>
      )}

      <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* AWAITING CONFIRMATION */}
        <section>
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            ⚠️ Təsdiq gözləyir ({pendingConfirm.length})
          </h2>
          {pendingConfirm.length === 0 ? (
            <div className="bg-stone-800/50 rounded-2xl p-8 text-center text-stone-400 border border-stone-700 text-sm">
              ✅ Yoxdur
            </div>
          ) : (
            <div className="space-y-3" data-testid="waiter-pending-confirm">
              {pendingConfirm.map((o) => {
                const sec = o.security_check || {};
                const geo = sec.geo || {};
                return (
                  <div key={o.id} className="bg-rose-600 rounded-2xl shadow-xl overflow-hidden animate-pulse-slow" data-testid={`pending-order-${o.id}`}>
                    <div className="bg-black/20 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-xs uppercase font-bold opacity-90">⚠️ Yeni sifariş</div>
                          <div className="text-2xl font-black">M{o.table_number} • {o.order_number}</div>
                        </div>
                        <div className="text-right text-xs opacity-80"><LiveTimestamp iso={o.created_at} now={now} /></div>
                      </div>

                      {/* Security info */}
                      <div className="text-[11px] space-y-0.5 mb-2 bg-black/20 rounded-lg p-2">
                        {geo.supplied && geo.distance_m != null ? (
                          <div className={geo.within_radius ? 'text-emerald-200' : 'text-yellow-100'}>
                            📍 Məsafə: ~{Math.round(geo.distance_m)}m {geo.within_radius ? '✓' : '⚠️ uzaqdadır'}
                          </div>
                        ) : geo.enabled ? (
                          <div className="text-yellow-100">⚠️ GPS göndərilməyib</div>
                        ) : null}
                        {o.customer_ip && <div className="opacity-70">🌐 {o.customer_ip}</div>}
                      </div>

                      <ul className="text-xs space-y-0.5 mb-3 max-h-24 overflow-y-auto">
                        {o.items.map((it, idx) => (
                          <li key={idx}>• {it.quantity}x {it.name_az || it.name_en}</li>
                        ))}
                      </ul>
                      <div className="text-xs mb-3 font-bold">Cəm: {o.currency_symbol}{o.total}</div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => confirmAction(o.id, 'confirm')}
                          disabled={resolvingId === o.id}
                          className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl text-sm disabled:opacity-50"
                          data-testid={`waiter-confirm-${o.id}`}
                        >{resolvingId === o.id ? '...' : '✓ Təsdiq'}</button>
                        <button
                          onClick={() => confirmAction(o.id, 'reject')}
                          disabled={resolvingId === o.id}
                          className="flex-1 py-2 bg-red-800 hover:bg-red-900 text-white font-bold rounded-xl text-sm disabled:opacity-50"
                          data-testid={`waiter-reject-${o.id}`}
                        >✕ Rədd</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* WAITER CALLS */}
        <section>
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            🛎️ Aktiv çağırışlar ({calls.length})
          </h2>
          {calls.length === 0 ? (
            <div className="bg-stone-800/50 rounded-2xl p-10 text-center text-stone-400 border border-stone-700">
              ✅ Heç bir çağırış yoxdur
            </div>
          ) : (
            <div className="space-y-3" data-testid="waiter-calls-list">
              {calls.map((c) => {
                const cfg = CALL_TYPE_LABEL[c.type] || CALL_TYPE_LABEL.call_waiter;
                return (
                  <div
                    key={c.id}
                    className={`rounded-2xl overflow-hidden shadow-xl ${cfg.color} animate-pulse-slow`}
                    data-testid={`waiter-call-${c.id}`}
                  >
                    <div className="p-4 bg-black/20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="text-4xl">{cfg.icon}</div>
                          <div>
                            <div className="text-xs uppercase opacity-90 font-bold">Masa</div>
                            <div className="text-4xl font-black">{c.table_number}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold">{cfg.label}</div>
                          <div className="text-xs opacity-80 mt-1"><LiveTimestamp iso={c.created_at} now={now} /></div>
                        </div>
                      </div>
                      {c.notes && <div className="mt-2 text-xs opacity-90">📝 {c.notes}</div>}
                      <button
                        onClick={() => resolveCall(c.id)}
                        disabled={resolvingId === c.id}
                        className="mt-3 w-full py-2.5 bg-white text-stone-900 font-bold rounded-xl hover:bg-stone-100 disabled:opacity-50"
                        data-testid={`resolve-call-${c.id}`}
                      >
                        {resolvingId === c.id ? '...' : '✓ Cavablandırdım'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* READY ORDERS to serve */}
        <section>
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            🍽️ Verilməyi gözləyir ({readyOrders.length})
          </h2>
          {readyOrders.length === 0 ? (
            <div className="bg-stone-800/50 rounded-2xl p-10 text-center text-stone-400 border border-stone-700">
              ✅ Hələ hazır sifariş yoxdur
            </div>
          ) : (
            <div className="space-y-3" data-testid="waiter-ready-orders">
              {readyOrders.map((o) => (
                <div key={o.id} className="bg-emerald-600 rounded-2xl shadow-xl overflow-hidden" data-testid={`ready-order-${o.id}`}>
                  <div className="bg-black/20 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="text-4xl">🍽️</div>
                        <div>
                          <div className="text-xs uppercase font-bold opacity-90">Masa</div>
                          <div className="text-4xl font-black">{o.table_number}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold">{o.order_number}</div>
                        <div className="text-xs opacity-80"><LiveTimestamp iso={o.created_at} now={now} /></div>
                      </div>
                    </div>
                    <ul className="text-xs space-y-0.5 mb-3 max-h-32 overflow-y-auto">
                      {o.items.map((it, idx) => (
                        <li key={idx}>
                          • {it.quantity}x {it.name_az || it.name_en}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => markServed(o.id)}
                      disabled={resolvingId === o.id}
                      className="w-full py-2.5 bg-white text-stone-900 font-bold rounded-xl hover:bg-stone-100 disabled:opacity-50"
                      data-testid={`mark-served-${o.id}`}
                    >
                      {resolvingId === o.id ? '...' : '✓ Verildi'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const Badge = ({ label, value, accent }) => (
  <div className="flex items-center gap-2">
    <div className={`${accent} text-white px-2.5 py-0.5 rounded-full text-sm font-bold min-w-[24px] text-center`}>{value}</div>
    <span className="text-xs text-stone-400 uppercase">{label}</span>
  </div>
);
